"use client";

import { c as _c } from "react/compiler-runtime";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { getImageProps } from "next/image";
import * as React from "react";
import cn from "twsx";
const avatarPixelSizes = {
  "3xs": 14,
  "2xs": 18,
  xs: 20,
  sm: 32,
  base: 40,
  lg: 48,
  xl: 64,
  "2xl": 80,
  "3xl": 96,
  "4xl": 112,
  "5xl": 160
} as const;
type AvatarSize = keyof typeof avatarPixelSizes;
const AvatarPixelSizeContext = React.createContext<number>(avatarPixelSizes.base);
export function getAvatarImagePixelSize(size: AvatarSize | null | undefined) {
  return avatarPixelSizes[size ?? "base"];
}
export function shouldOptimizeAvatarImage(src: string) {
  try {
    const {
      hostname,
      protocol
    } = new URL(src);
    return protocol === "https:" && hostname.endsWith(".public.blob.vercel-storage.com");
  } catch {
    return false;
  }
}

// Sizes follow the Figma avatar scale. `square` is the 4px-radius workspace
// and organization mark; `frame: "none"` drops the hairline for avatars that
// sit on a tinted chip.
const avatarVariants = cva("relative flex shrink-0 overflow-hidden", {
  variants: {
    variant: {
      rounded: "rounded-full",
      square: "rounded-kbd"
    },
    size: {
      "3xs": "size-3.5",
      "2xs": "size-4.5",
      xs: "size-5",
      sm: "size-8",
      base: "size-10",
      lg: "size-12",
      xl: "size-16",
      "2xl": "size-20",
      "3xl": "size-24",
      "4xl": "size-28",
      "5xl": "size-32 sm:size-40"
    },
    frame: {
      hairline: "[border-color:var(--avatar-border,var(--color-border-secondary))] [border-width:var(--border-width-default)]",
      none: ""
    }
  },
  defaultVariants: {
    variant: "rounded",
    size: "base",
    frame: "hairline"
  }
});
export type AvatarProps = React.ComponentProps<typeof AvatarPrimitive.Root> & VariantProps<typeof avatarVariants>;

/** Use when showing a person, team or organization image with initials as fallback. */
const Avatar = t0 => {
  const $ = _c(23);
  let className;
  let frame;
  let props;
  let ref;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      size: t2,
      frame,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = frame;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
    $[6] = t2;
  } else {
    className = $[1];
    frame = $[2];
    props = $[3];
    ref = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const variant = t1 === undefined ? "rounded" : t1;
  const size = t2 === undefined ? "base" : t2;
  let t3;
  if ($[7] !== size) {
    t3 = getAvatarImagePixelSize(size);
    $[7] = size;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const pixelSize = t3;
  let t4;
  if ($[9] !== className || $[10] !== frame || $[11] !== size || $[12] !== variant) {
    t4 = cn("group/avatar", avatarVariants({
      variant,
      size,
      frame,
      className
    }));
    $[9] = className;
    $[10] = frame;
    $[11] = size;
    $[12] = variant;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== props || $[15] !== ref || $[16] !== size || $[17] !== t4 || $[18] !== variant) {
    t5 = <AvatarPrimitive.Root ref={ref} className={t4} data-avatar={true} data-size={size} data-variant={variant} {...props} />;
    $[14] = props;
    $[15] = ref;
    $[16] = size;
    $[17] = t4;
    $[18] = variant;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== pixelSize || $[21] !== t5) {
    t6 = <AvatarPixelSizeContext.Provider value={pixelSize}>{t5}</AvatarPixelSizeContext.Provider>;
    $[20] = pixelSize;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
};
Avatar.displayName = AvatarPrimitive.Root.displayName;
const AvatarImage = t0 => {
  const $ = _c(17);
  let className;
  let props;
  let ref;
  let src;
  let t1;
  if ($[0] !== t0) {
    ({
      alt: t1,
      className,
      ref,
      src,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = src;
    $[5] = t1;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    src = $[4];
    t1 = $[5];
  }
  const alt = t1 === undefined ? "" : t1;
  const pixelSize = React.useContext(AvatarPixelSizeContext);
  let t2;
  if ($[6] !== alt || $[7] !== pixelSize || $[8] !== src) {
    t2 = typeof src === "string" && shouldOptimizeAvatarImage(src) ? getImageProps({
      alt,
      height: pixelSize,
      quality: 75,
      src,
      width: pixelSize
    }).props : {
      alt,
      src
    };
    $[6] = alt;
    $[7] = pixelSize;
    $[8] = src;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const optimizedImageProps = t2;
  let t3;
  if ($[10] !== className) {
    t3 = cn("h-full w-full object-cover", className);
    $[10] = className;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== optimizedImageProps || $[13] !== props || $[14] !== ref || $[15] !== t3) {
    t4 = <AvatarPrimitive.Image ref={ref} className={t3} {...optimizedImageProps} {...props} />;
    $[12] = optimizedImageProps;
    $[13] = props;
    $[14] = ref;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
};
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

// `tone` tints initials for system avatars (inbox sources, workspace marks);
// `layout: "placeholder"` stacks an icon over a short caption in large photo slots.
const avatarFallbackVariants = cva(["flex h-full w-full items-center justify-center font-medium leading-none", "group-data-[variant=rounded]/avatar:rounded-full group-data-[variant=square]/avatar:rounded-kbd", "group-data-[size=3xs]/avatar:text-2xs group-data-[size=2xs]/avatar:text-2xs group-data-[size=xs]/avatar:text-2xs", "group-data-[size=sm]/avatar:text-xs group-data-[size=base]/avatar:text-sm", "group-data-[size=lg]/avatar:text-lg group-data-[size=xl]/avatar:text-xl", "group-data-[size=2xl]/avatar:text-2xl group-data-[size=3xl]/avatar:text-3xl group-data-[size=4xl]/avatar:text-4xl", "group-data-[size=5xl]/avatar:text-5xl"], {
  variants: {
    tone: {
      neutral: "bg-bg-secondary text-text-primary",
      accent: "bg-accent-tint text-text-primary",
      info: "bg-blue-bg-subtle text-blue-text",
      success: "bg-green-bg-subtle text-green-text",
      warning: "bg-bg-secondary text-yellow-strong",
      danger: "bg-red-bg-subtle text-red-text",
      brand: "bg-accent text-text-on-accent"
    },
    layout: {
      initials: "",
      placeholder: "flex-col gap-2 px-4 text-center text-base font-normal leading-5 text-text-secondary [&>svg]:size-6"
    }
  },
  defaultVariants: {
    tone: "neutral",
    layout: "initials"
  }
});
const AvatarFallback = t0 => {
  const $ = _c(14);
  let className;
  let layout;
  let props;
  let ref;
  let tone;
  if ($[0] !== t0) {
    ({
      className,
      tone,
      layout,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = layout;
    $[3] = props;
    $[4] = ref;
    $[5] = tone;
  } else {
    className = $[1];
    layout = $[2];
    props = $[3];
    ref = $[4];
    tone = $[5];
  }
  let t1;
  if ($[6] !== className || $[7] !== layout || $[8] !== tone) {
    t1 = cn(avatarFallbackVariants({
      tone,
      layout
    }), className);
    $[6] = className;
    $[7] = layout;
    $[8] = tone;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== props || $[11] !== ref || $[12] !== t1) {
    t2 = <AvatarPrimitive.Fallback ref={ref} className={t1} {...props} />;
    $[10] = props;
    $[11] = ref;
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
};
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

// A 15px round status glyph pinned to the avatar's bottom-right corner.
/** Use when an avatar needs a small status mark on its corner. */
const AvatarBadge = t0 => {
  const $ = _c(6);
  const {
    badge,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("absolute -bottom-0.5 -right-0.5 flex size-3.75 items-center justify-center", "rounded-full border border-bg-surface bg-bg-secondary text-text-tertiary [&>svg]:size-3");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== badge) {
    t2 = <span className={t1}>{badge}</span>;
    $[1] = badge;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== t2) {
    t3 = <span className="relative inline-flex shrink-0">{children}{t2}</span>;
    $[3] = children;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
export { Avatar, AvatarBadge, AvatarFallback, AvatarImage };